import React, { useId } from 'react';
import clsx from 'clsx';

type Base = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>;

export interface InputProps extends Base {
    label?: string;
    error?: string;
    helperText?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    fullWidth?: boolean;
    /** 50px (md) · 56px (lg) · 44px (sm) */
    fieldSize?: 'sm' | 'md' | 'lg';
    /** Tabular, heavy figures for amounts / phone numbers */
    numeric?: boolean;
    /** Render a textarea with the same chrome */
    multiline?: boolean;
    rows?: number;
    /** Extra classes for the bordered wrapper */
    wrapperClassName?: string;
}

const heights = { sm: 'h-11', md: 'h-[50px]', lg: 'h-14' };

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({ label, error, helperText, leftIcon, rightIcon, fullWidth = true, fieldSize = 'md', numeric = false, multiline = false, rows = 3, className, wrapperClassName, id, disabled, ...props }, ref) => {
        const autoId = useId();
        const inputId = id || `in-${autoId}`;
        const describedBy = error ? `${inputId}-error` : helperText ? `${inputId}-hint` : undefined;

        const control = clsx(
            'min-w-0 flex-1 bg-transparent outline-none placeholder:text-ink-muted placeholder:font-normal text-ink-primary disabled:cursor-not-allowed',
            numeric ? 'tnum text-xl font-extrabold tracking-[.02em]' : 'text-base font-medium',
            multiline ? 'py-3 resize-none leading-snug' : 'h-full',
            className,
        );

        return (
            <div className={clsx('flex flex-col gap-2', fullWidth && 'w-full')}>
                {label && (
                    <label htmlFor={inputId} className="text-[13px] font-extrabold text-ink-secondary">
                        {label}
                        {props.required && <span className="ml-0.5 text-status-danger">*</span>}
                    </label>
                )}
                <div
                    className={clsx(
                        'flex items-center gap-2.5 rounded-md border bg-surface-card px-[18px] transition-[border-color,box-shadow] duration-200',
                        multiline ? 'items-start' : heights[fieldSize],
                        error ? 'border-status-danger focus-within:shadow-[0_0_0_4px_var(--status-danger-surface)]' : 'border-line focus-within:border-accent-500 focus-within:shadow-focus',
                        'focus-within:border-2 focus-within:px-[17px]',
                        disabled && 'opacity-55 bg-surface-sunken pointer-events-none',
                        wrapperClassName,
                    )}
                >
                    {leftIcon && <span className={clsx('inline-flex shrink-0 text-ink-muted [&>svg]:w-[18px] [&>svg]:h-[18px]', multiline && 'mt-3')}>{leftIcon}</span>}
                    {multiline ? (
                        <textarea
                            id={inputId}
                            rows={rows}
                            disabled={disabled}
                            aria-invalid={!!error}
                            aria-describedby={describedBy}
                            className={control}
                            {...(props as unknown as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
                        />
                    ) : (
                        <input
                            ref={ref}
                            id={inputId}
                            disabled={disabled}
                            aria-invalid={!!error}
                            aria-describedby={describedBy}
                            className={control}
                            {...props}
                        />
                    )}
                    {rightIcon && <span className="inline-flex shrink-0 text-ink-muted [&>svg]:w-[18px] [&>svg]:h-[18px]">{rightIcon}</span>}
                </div>
                {error && <p id={`${inputId}-error`} className="text-xs font-semibold text-status-danger">{error}</p>}
                {helperText && !error && <p id={`${inputId}-hint`} className="text-xs text-ink-muted">{helperText}</p>}
            </div>
        );
    },
);
Input.displayName = 'Input';
export default Input;
